import {
  CloseOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Button, Image, Tooltip, Upload } from "antd";
import type { UploadFile, UploadProps } from "antd";
import type { RcFile } from "antd/es/upload";
import type { UploadRef } from "antd/es/upload/Upload";
import {
  FC,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { docFetch } from "@/apis/doc";
import { getFileUrl } from "@/apis/file";
import {
  downloadFile,
  getFileIcon,
  handlePreviewFile,
  image,
  video,
} from "@/components/SDoc/utils";
import { Doc, DocTypeEnum } from "@/types/doc";
import { WFile } from "@/types/file";
import { upload } from "@/utils/file/upload";
import VideoPreviewModal from "@/components/VideoPreviewModal";
import { getLogAttachments } from "./model";
import { LogAttachmentsWrap } from "./style";

interface LogAttachmentsProps {
  docId?: string;
  doc?: Doc | null;
  files?: UploadFile<WFile>[];
  disabled?: boolean;
  onRemove?: (uid: string) => void;
  displayMode?: "compact" | "list";
}

interface LogAttachmentUploadProps {
  fileList: UploadFile<WFile>[];
  disabled?: boolean;
  onChange: (fileList: UploadFile<WFile>[]) => void;
}

export interface LogAttachmentUploadHandle {
  addFiles: (files: FileList) => void;
}

interface DisplayAttachment {
  key: string;
  name: string;
  suffix: string;
  fileId: string;
  nodeType: DocTypeEnum;
  uploading?: boolean;
  uid?: string;
}

const getFileSuffix = (name: string) =>
  name.split(".").pop()?.toLowerCase() ?? "";

export const LogAttachmentUpload = forwardRef<
  LogAttachmentUploadHandle,
  LogAttachmentUploadProps
>(function LogAttachmentUpload({ fileList, disabled = false, onChange }, ref) {
  const { t } = useTranslation();
  const uploadRef = useRef<UploadRef<WFile>>(null);

  useImperativeHandle(ref, () => ({
    addFiles: (files) => {
      if (disabled || !files.length) return;

      const fileInput = uploadRef.current?.nativeElement?.querySelector<HTMLInputElement>(
        'input[type="file"]',
      );
      if (!fileInput) return;

      // 将拖入文件交给隐藏 input，复用 Upload 的上传队列和状态更新。
      fileInput.files = files;
      // change 事件需冒泡到 React 的事件监听器，才能触发 Upload 的 onChange。
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    },
  }), [disabled]);

  const customRequest: UploadProps<WFile>["customRequest"] = async (
    options,
  ) => {
    const file = options.file as RcFile;

    try {
      const uploadedFile = await upload({
        name: file.name,
        size: file.size,
        file,
      });

      if (!uploadedFile) throw new Error("File upload failed");
      options.onSuccess?.(uploadedFile);
    } catch (error) {
      options.onError?.(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  };

  const handleChange: UploadProps<WFile>["onChange"] = ({ file, fileList }) => {
    onChange(
      file.status === "error"
        ? fileList.filter((item) => item.uid !== file.uid)
        : fileList,
    );
  };

  return (
    <Upload<WFile>
      ref={uploadRef}
      multiple
      disabled={disabled}
      fileList={fileList}
      showUploadList={false}
      customRequest={customRequest}
      onChange={handleChange}
    >
      <Button
        size="small"
        type="primary"
        disabled={disabled}
        aria-label={t("components.upload")}
      >
        {t("components.upload")}
      </Button>
    </Upload>
  );
});

const LogAttachments: FC<LogAttachmentsProps> = ({
  docId,
  doc,
  files = [],
  disabled = false,
  onRemove,
  displayMode = "compact",
}) => {
  const { t } = useTranslation();
  const [resolvedDoc, setResolvedDoc] = useState<Doc | null>(doc ?? null);
  const [previewImage, setPreviewImage] = useState<DisplayAttachment | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<DisplayAttachment | null>(null);

  useEffect(() => {
    setResolvedDoc(doc ?? null);
    if (!docId || getLogAttachments(doc).length > 0) return;

    let active = true;
    docFetch(docId)
      .then((nextDoc) => {
        if (active) setResolvedDoc(nextDoc);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [doc, docId]);

  const attachments: DisplayAttachment[] = [
    ...getLogAttachments(resolvedDoc).map((item) => ({
      key: `doc-${item.id}`,
      name: item.name,
      suffix: item.suffix,
      fileId: item.fileId,
      nodeType: item.nodeType,
    })),
    ...files
      .filter((file) => file.status === "uploading" || !!file.response)
      .map((file) => ({
        key: `file-${file.uid}`,
        name: file.response?.name || file.name,
        suffix: file.response?.suffix || getFileSuffix(file.name),
        fileId: file.response?.id || "",
        nodeType: DocTypeEnum.LEAF,
        uploading: file.status === "uploading",
        uid: file.uid,
      })),
  ];

  const openAttachment = (attachment: DisplayAttachment) => {
    if (!attachment.fileId || attachment.uploading) return;

    const suffix = (attachment.suffix || getFileSuffix(attachment.name)).toLowerCase();
    if (image.includes(suffix)) {
      setPreviewImage(attachment);
      setPreviewOpen(true);
      return;
    }

    if (video.some((type) => type.trim().toLowerCase() === suffix)) {
      setPreviewVideo(attachment);
      return;
    }

    handlePreviewFile(attachment);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    attachment: DisplayAttachment,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openAttachment(attachment);
    }
  };

  const handleDownload = (
    event: MouseEvent<HTMLButtonElement>,
    fileId: string,
  ) => {
    event.stopPropagation();
    downloadFile(fileId);
  };

  if (attachments.length === 0) return null;

  const imagePreview = previewImage ? (
    <Image
      wrapperStyle={{ display: "none" }}
      src={getFileUrl(previewImage.fileId) ?? ""}
      alt={previewImage.name}
      preview={{
        visible: previewOpen,
        onVisibleChange: setPreviewOpen,
        afterOpenChange: (visible) => !visible && setPreviewImage(null),
      }}
    />
  ) : null;

  const videoPreview = previewVideo ? (
    <VideoPreviewModal
      ariaLabel={previewVideo.name}
      open
      src={getFileUrl(previewVideo.fileId) ?? ""}
      onClose={() => setPreviewVideo(null)}
    />
  ) : null;

  if (displayMode === "list") {
    return (
      <LogAttachmentsWrap className="log-attachments--list">
        {imagePreview}
        {videoPreview}
        {attachments.map((attachment) => (
          <div className="log-attachment--row" key={attachment.key}>
            <button
              type="button"
              className="log-attachment--main"
              onClick={() => openAttachment(attachment)}
              title={attachment.name}
            >
              <img
                className="log-attachment--row-icon"
                alt=""
                src={getFileIcon(attachment.name, attachment.nodeType)}
              />
              <span className="log-attachment--name">{attachment.name}</span>
            </button>
            <Tooltip title={t("common.download")}>
              <button
                type="button"
                className="log-attachment--row-download"
                onClick={(event) =>
                  handleDownload(event, attachment.fileId)
                }
                aria-label={t("common.download")}
              >
                <DownloadOutlined />
              </button>
            </Tooltip>
          </div>
        ))}
      </LogAttachmentsWrap>
    );
  }

  return (
    <LogAttachmentsWrap>
      {imagePreview}
      {videoPreview}
      {attachments.map((attachment) => (
        <Tooltip key={attachment.key} title={attachment.name}>
          <div
            className={`log-attachment--tile${
              attachment.uploading ? " log-attachment--uploading" : ""
            }`}
            role="button"
            tabIndex={attachment.uploading ? -1 : 0}
            aria-busy={attachment.uploading}
            onClick={() => !attachment.uploading && openAttachment(attachment)}
            onKeyDown={(event) =>
              !attachment.uploading && handleKeyDown(event, attachment)
            }
          >
            <img
              alt=""
              src={getFileIcon(attachment.name, attachment.nodeType)}
            />
            {attachment.uploading && (
              <span className="log-attachment--loading">
                <LoadingOutlined spin />
              </span>
            )}
            {!attachment.uploading && onRemove && attachment.uid && (
              <button
                type="button"
                className="log-attachment--corner-action log-attachment--remove"
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(attachment.uid as string);
                }}
                aria-label={t("common.remove")}
                title={t("common.remove")}
              >
                <CloseOutlined />
              </button>
            )}
            {!attachment.uploading && !onRemove && (
              <button
                type="button"
                className="log-attachment--corner-action log-attachment--download"
                onClick={(event) =>
                  handleDownload(event, attachment.fileId)
                }
                aria-label={t("common.download")}
                title={t("common.download")}
              >
                <DownloadOutlined />
              </button>
            )}
          </div>
        </Tooltip>
      ))}
    </LogAttachmentsWrap>
  );
};

export default LogAttachments;
